import { useChartCompareSetSelectContext } from "components/charting/contexts/ChartCompareSetSelectContext";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import { useChartCompareProfileSelectContext } from "components/charting/contexts/ChartCompareProfileSelectContext";
import SetSelector, { type SetSelectorOption } from "components/Inputs/SetSelector";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material/Autocomplete";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import { useChartSetSelectStatusContext } from "components/charting/contexts/ChartSetSelectStatusContext";
import { useChartProfileSelectStatusContext } from "components/charting/contexts/ChartProfileSelectStatusContext";
import useStatusBuilder from "hooks/useStatusBuilder";

export interface AthleteProfileChartProfilingCompareSetSelectorProps {}

function AthleteProfileChartProfilingCompareSetSelector(props: AthleteProfileChartProfilingCompareSetSelectorProps) {
  const { t } = useTranslation();
  const { value, options, onChange } = useChartCompareSetSelectContext();
  const {
    value: profiles,
    options: profileOptions,
    onChange: onProfilesChange,
  } = useChartCompareProfileSelectContext();
  const { value: selectedPrimarySets } = useChartSetSelectContext();
  const { value: selectedPrimaryProfiles } = useChartProfileSelectContext();

  const setStatus = useChartSetSelectStatusContext();
  const profileStatus = useChartProfileSelectStatusContext();

  const status = useStatusBuilder(useMemo(() => [setStatus, profileStatus], [setStatus, profileStatus]));

  const combinedOptions = useMemo<SetSelectorOption[]>(
    () => [...profileOptions, ...options],
    [profileOptions, options]
  );
  const combinedValue = useMemo<SetSelectorOption[]>(() => [...profiles, ...value], [profiles, value]);

  const getOptionDisabled = useCallback(
    (option: SetSelectorOption) => {
      if (LoadVelocityProfileHelpers.isLoadVelocityProfile(option)) {
        return selectedPrimaryProfiles.includes(option);
      }
      return selectedPrimarySets.includes(option);
    },
    [selectedPrimaryProfiles, selectedPrimarySets]
  );

  // Baseline picks can't be compared against themselves, but still show as picked.
  const getOptionChecked = useCallback(
    (option: SetSelectorOption) =>
      LoadVelocityProfileHelpers.isLoadVelocityProfile(option)
        ? selectedPrimaryProfiles.includes(option)
        : selectedPrimarySets.includes(option),
    [selectedPrimaryProfiles, selectedPrimarySets]
  );

  const handleChange = useCallback(
    (
      _: unknown,
      selected: SetSelectorOption[],
      _reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<SetSelectorOption>
    ) => {
      const { profiles, sets } = LoadVelocityProfileHelpers.partitionSelection(selected, details?.option);
      onProfilesChange(profiles);
      onChange(sets);
    },
    [onChange, onProfilesChange]
  );

  const TextFieldProps = useMemo(
    () => ({
      label: t("athlete_profile_chart_compare_set_selector.compare_set_select_label"),
      placeholder: combinedValue.length ? undefined : t("global.search_sets"),
    }),
    [combinedValue.length, t]
  );

  return (
    <SetSelector<SetSelectorOption>
      value={combinedValue}
      options={combinedOptions}
      onChange={handleChange}
      TextFieldProps={TextFieldProps}
      getOptionDisabled={getOptionDisabled}
      getOptionChecked={getOptionChecked}
      status={status}
    />
  );
}

export default AthleteProfileChartProfilingCompareSetSelector;
